import { FC, useState } from "react";
import "./CSS/FAQDetailScreen.css";
import backIcon from "../../assets/images/back-icon.svg";
import { useNavigate } from "react-router-dom";
import FAQExpandableSection from "./Views/FAQExpandableSection";

const FAQDetailScreen: FC = () => {
  const navigate = useNavigate();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const handleToggle = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  const handleBackButtonClick = () => {
    navigate(-1);
  };

  return (
    <div className="wms-mobile-container">
      <div className="faq-detail-screen-container">
        <div className="faq-detail-header-container">
          <img
            src={backIcon}
            alt="dropdown-logo"
            className="image"
            onClick={handleBackButtonClick}
          />
          <div className="title">FAQ</div>
        </div>
        <FAQExpandableSection
          title="What is SabeHub?"
          index={1}
          expanded={expandedIndex === 1}
          onToggle={() => handleToggle(1)}
        >
          <div className="item-container">
            <span>
              SabeHub is a <strong>3rd Space ecosystem</strong> built for
              Founders, Remote Workers, & Ambitious Professionals who want a
              better environment than home, cafés, or noisy offices.
              <br />
              <br />
              It brings together everything needed for a stronger workday:
              Focused Execution, Clean Food and Drinks, Wellness, Recovery,
              Curated Experiences, and Community. At SabeHub, your day is
              designed around one idea:
              <br />
              <br />
              <strong>
                Think deeply. Execute clearly. Fuel cleanly. Recover
                intentionally. Grow sustainably.
              </strong>
              <br />
              <br />
              It is not just a place to sit and work. It is a workday system
              built to help you protect your energy, regain your time, and move
              forward with more clarity.
            </span>
          </div>
        </FAQExpandableSection>
        <FAQExpandableSection
          title="What is included in a SabeHub membership?"
          index={2}
          expanded={expandedIndex === 2}
          onToggle={() => handleToggle(2)}
        >
          <div className="item-container">
            <span>
              A SabeHub membership gives you access to the{" "}
              <strong>full ecosystem</strong> designed to help you regain your
              energy and time.
              <br />
              Depending on your membership plan, it may include access to:
              <br />
              <ul>
                <li>Execution Space for focused work</li>
                <ul>
                  <li>Create, Innovate, and Communicate zones</li>
                </ul>
                <li>
                  Energy Space with clean food, coffee, matcha, and drinks
                </li>
                <li>Wellness and recovery sessions</li>
                <li>Curated experiences and member events</li>
                <li>Founder conversations and community formats</li>
                <li>Smart scaling workshops</li>
                <li>Private member experiences</li>
                <li>The Foundry Community access, where applicable</li>
              </ul>
              Each membership is designed to support how you work, fuel,
              recover, and connect — not just how many hours you spend at a
              desk.
              <br /> Full membership inclusions depend on your selected plan and
              can be reviewed on the SabeHub website.
            </span>
          </div>
        </FAQExpandableSection>
        <FAQExpandableSection
          title="What is The Foundry?"
          index={3}
          expanded={expandedIndex === 3}
          onToggle={() => handleToggle(3)}
        >
          <div className="item-container">
            <span>
              The Foundry is{" "}
              <strong>SabeHub’s Private Founder Community</strong> for founders
              and business owners.
              <br /> It is built for serious builders who want focused work,
              better conversations, practical learning, founder support, and
              real relationships with people who understand business, pressure,
              ambition, and growth.
              <br /> SabeHub is the ecosystem.
              <br /> The Foundry is the founder-only community layer within it.
            </span>
          </div>
        </FAQExpandableSection>
        <FAQExpandableSection
          title="Can I use my credits across Execution, wellness, and experiences?"
          index={4}
          expanded={expandedIndex === 4}
          onToggle={() => handleToggle(4)}
        >
          <div className="item-container">
            <span>
              Yes. Your membership credits can be used across eligible SabeHub
              experiences, depending on your membership plan.
              <br /> This may include Execution Space access, Wellness sessions,
              and curated Experiences.
              <br /> By default, credits are allocated based on how members
              usually receive the most value. However, the ecosystem is designed
              to stay flexible wherever possible.
              <br /> Some retreats, exclusive experiences, private formats, or
              special programs may have separate access criteria or pricing.
            </span>
          </div>
        </FAQExpandableSection>
        <FAQExpandableSection
          title="How often do members meet?"
          index={5}
          expanded={expandedIndex === 5}
          onToggle={() => handleToggle(5)}
        >
          <div className="item-container">
            <span>
              SabeHub is designed for consistent, natural, and meaningful
              connection — not random networking. Members meet through the
              monthly rhythm of the ecosystem:
              <br />
              <ul>
                <li>Founder brunches</li>
                <li>Private dinners</li>
                <li>Sports experiences</li>
                <li>Power workshops</li>
                <li>Member-only meetups</li>
                <li>Peer-led sessions</li>
                <li>Wellness and recovery sessions</li>
              </ul>
              As part of the community rhythm, members can expect at least{" "}
              <strong>2 curated experiences every month</strong> , along with
              additional free and paid workshops, special sessions, and
              community formats.
              <br /> The goal is not to collect business cards.
              <br /> The goal is to build trust, relationships, clarity, and
              momentum over time.
            </span>
          </div>
        </FAQExpandableSection>
        <FAQExpandableSection
          title="What kind of experiences will members get access to?"
          index={6}
          expanded={expandedIndex === 6}
          onToggle={() => handleToggle(6)}
        >
          <div className="item-container">
            <span>
              Members get access to a curated calendar of experiences designed
              to help them{" "}
              <strong>
                learn faster, connect better, and reset with the right people.
              </strong>{" "}
              <br />
              Experiences may include:
              <ul>
                <li>Brunch clubs</li>
                <li>Founder dinners</li>
                <li>Sports clubs</li>
                <li>Power workshops</li>
                <li>Founder circles</li>
                <li>Peer-led discussions</li>
                <li>Member-only meetups</li>
                <li>Wellness-led community sessions</li>
                <li>Special founder formats and invite-only gatherings</li>
              </ul>
              Each experience is intentionally designed so members leave with
              better relationships, sharper thinking, practical insights, and
              renewed energy.
            </span>
          </div>
        </FAQExpandableSection>
        <FAQExpandableSection
          title="What is the Energy Space?"
          index={7}
          expanded={expandedIndex === 7}
          onToggle={() => handleToggle(7)}
        >
          <div className="item-container">
            <span>
              The Energy Space is SabeHub’s clean fuel café.
              <br /> It is built for people who do not want food to slow them
              down during the workday.
              <br />
              The Energy Space focuses on real ingredients, fresh preparation,
              clean food, specialty coffee, premium Japanese matcha, fresh
              drinks, handmade food, and no packaged shortcuts.
              <br /> Every meal, coffee, and drink is designed to help you feel
              lighter, clearer, and more focused.
              <br /> At SabeHub, food and drinks are not an afterthought.
              <br /> They are part of your energy system.
            </span>
          </div>
        </FAQExpandableSection>
        <FAQExpandableSection
          title="What is included in Wellness?"
          index={8}
          expanded={expandedIndex === 8}
          onToggle={() => handleToggle(8)}
        >
          <div className="item-container">
            <span>
              Wellness is an integrated part of SabeHub’s ecosystem.
              <br /> It is designed to help members reset, recover, and return
              to work with more clarity and energy.
              <br /> Offerings may include:
              <ul>
                <li>Yoga</li>
                <li>Breathwork</li>
                <li>Meditation</li>
                <li>Pilates</li>
                <li>Cross Training</li>
                <li>Mind-body recovery sessions</li>
              </ul>
              Sessions are planned in monthly morning and evening formats.
              <br /> The purpose is not just fitness. It is to support focus,
              nervous system regulation, sustainable performance, and a better
              workday rhythm.
            </span>
          </div>
        </FAQExpandableSection>
        <FAQExpandableSection
          title="Can I take a tour before joining?"
          index={9}
          expanded={expandedIndex === 9}
          onToggle={() => handleToggle(9)}
        >
          <div className="item-container">
            <span>
              Yes. You can visit SabeHub in person or explore the space through
              a virtual tour before joining.
              <br /> The tour helps you understand the Execution Space, Energy
              Space, Wellness Space, and community environment before becoming a
              member.
              <br /> It is the best way to see how SabeHub is designed to
              support your workday, energy, and growth.
            </span>
          </div>
        </FAQExpandableSection>
        <FAQExpandableSection
          title="What makes SabeHub different from a coworking space?"
          index={10}
          expanded={expandedIndex === 10}
          onToggle={() => handleToggle(10)}
        >
          <div className="item-container">
            <span>
              Most coworking spaces provide desks.
              <br /> SabeHub is built as a complete workday ecosystem. It
              supports the full rhythm of a high-performance day. You can think
              in the Create Space, execute in the Innovate Space, meet in the
              Communicate Space, fuel in the Energy Space, recover in the
              Wellness Space, and connect through curated experiences. <br />
              Instead of only giving you a seat, SabeHub helps you work with
              better energy, fewer distractions, stronger routines, and the
              right people around you.
            </span>
          </div>
        </FAQExpandableSection>
      </div>
    </div>
  );
};

export default FAQDetailScreen;
